/**
 * マッチメイキングサービス。
 *
 * Hub API: `/games/:gameId/matchmaking/tickets` 系エンドポイントを叩く。
 *   - POST  /...              — チケット作成（待機開始）
 *   - GET   /.../:ticketId    — チケット状態取得（SEARCHING / MATCHED / TIMED_OUT / CANCELLED）
 *   - DELETE /.../:ticketId   — チケットキャンセル（冪等）
 *
 * フォールバックポリシー（sdk-parity.md 準拠 / antipatterns.md §1）:
 *   - signed_in (preferFetch=true): ymgFetch 一本で叩く。失敗は throw。
 *     SDK fallback には倒さない（同じ Hub に同じ認証で叩くため、再試行で
 *     結果が変わらない上、409 Duplicate ticket を二重発火するリスクがある）。
 *   - それ以外: SDK メソッドが存在すれば優先、なければ ymgFetch。
 *   - 致命エラーは常に上流へ伝播。呼び出し側（hook / UI）でハンドリングする。
 */

import { getYmgSdk, getGameId, ymgFetch, hasCapacitorSessionToken } from './sdk'
import { getAuthState } from './auth'
import {
  MATCHMAKING_POLL_INTERVAL_MS,
  MATCHMAKING_TIMEOUT_MS,
  MATCH_BUILD_DEFAULT,
  MATCH_MODE_DEFAULT,
  MATCH_REGION_DEFAULT,
  MULTIPLAYER_MAX_PARTY_SIZE,
  type MatchmakingStatus,
  type MatchmakingTicket,
  type RoomInfo,
} from '../config/multiplayer'
import { normalizeRoom } from './rooms'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CreateTicketParams {
  mode?: string
  region?: string
  build?: string
  partySize?: number
  timeoutSec?: number
}

export interface PollUntilMatchedOptions {
  signal?: AbortSignal
  onStatusUpdate?: (status: MatchmakingStatus) => void
  intervalMs?: number
  timeoutMs?: number
}

export class MatchmakingTimeoutError extends Error {
  constructor(message = 'matchmaking timed out') {
    super(message)
    this.name = 'MatchmakingTimeoutError'
  }
}

export class MatchmakingCancelledError extends Error {
  constructor(message = 'matchmaking cancelled') {
    super(message)
    this.name = 'MatchmakingCancelledError'
  }
}

// ---------------------------------------------------------------------------
// Normalizers
// ---------------------------------------------------------------------------

function normalizeStatus(raw: unknown): MatchmakingStatus {
  const v = typeof raw === 'string' ? raw.toUpperCase() : ''
  if (v === 'SEARCHING' || v === 'MATCHED' || v === 'TIMED_OUT' || v === 'CANCELLED') {
    return v
  }
  // SDK が省略した場合は SEARCHING とみなす（API ドキュメントでは status 省略時 SEARCHING）
  return 'SEARCHING'
}

function toTicket(
  ticketId: string,
  expiresAt: number,
  status: MatchmakingStatus,
  room: RoomInfo | null,
): MatchmakingTicket {
  return { ticketId, expiresAt, status, room }
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

/**
 * マッチメイキングチケットを作成する。Hub は即時マッチ成立時に room を同封して返す。
 *
 * @throws Hub API エラー（401, 409 Duplicate ticket 等）は上流に伝播。
 */
export async function createTicket(
  params: CreateTicketParams = {},
): Promise<MatchmakingTicket> {
  const gameId = getGameId()
  const preferFetch = getAuthState().status === 'signed_in' || (await hasCapacitorSessionToken())
  const body = {
    mode: params.mode ?? MATCH_MODE_DEFAULT,
    region: params.region ?? MATCH_REGION_DEFAULT,
    build: params.build ?? MATCH_BUILD_DEFAULT,
    partySize: params.partySize ?? MULTIPLAYER_MAX_PARTY_SIZE,
    timeoutSec: params.timeoutSec ?? Math.round(MATCHMAKING_TIMEOUT_MS / 1000),
  }

  // signed_in: ymgFetch 一本（SDK fallback で 409 Duplicate ticket を二重発火させない）
  if (preferFetch) {
    const res = await ymgFetch<YmgMatchmakingTicketCreateResponse>(
      `/games/${encodeURIComponent(gameId)}/matchmaking/tickets`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    )
    return toTicket(
      res.ticketId,
      res.expiresAt,
      normalizeStatus(res.status),
      res.room ? normalizeRoom(res.room) : null,
    )
  }

  // ゲスト: SDK メソッドがあれば優先、無ければ ymgFetch
  const sdk = await getYmgSdk()
  const sdkApi = sdk.api as unknown as {
    createMatchmakingTicket?: (p: {
      gameId: string
      mode: string
      region: string
      build: string
      partySize?: number
      timeoutSec?: number
    }) => Promise<YmgMatchmakingTicketCreateResponse>
  }
  if (typeof sdkApi.createMatchmakingTicket === 'function') {
    const res = await sdkApi.createMatchmakingTicket({ gameId, ...body })
    return toTicket(
      res.ticketId,
      res.expiresAt,
      normalizeStatus(res.status),
      res.room ? normalizeRoom(res.room) : null,
    )
  }

  const res = await ymgFetch<YmgMatchmakingTicketCreateResponse>(
    `/games/${encodeURIComponent(gameId)}/matchmaking/tickets`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  )
  return toTicket(
    res.ticketId,
    res.expiresAt,
    normalizeStatus(res.status),
    res.room ? normalizeRoom(res.room) : null,
  )
}

/**
 * チケットの状態を取得する。ポーリングで呼ぶ。
 */
export async function getTicketStatus(
  ticketId: string,
): Promise<{ status: MatchmakingStatus; room: RoomInfo | null }> {
  const gameId = getGameId()
  const preferFetch = getAuthState().status === 'signed_in' || (await hasCapacitorSessionToken())

  if (preferFetch) {
    const res = await ymgFetch<YmgMatchmakingTicketStatusResponse>(
      `/games/${encodeURIComponent(gameId)}/matchmaking/tickets/${encodeURIComponent(ticketId)}`,
      { method: 'GET' },
    )
    return {
      status: normalizeStatus(res.status),
      room: res.room ? normalizeRoom(res.room) : null,
    }
  }

  const sdk = await getYmgSdk()
  const sdkApi = sdk.api as unknown as {
    getMatchmakingTicket?: (p: {
      gameId: string
      ticketId: string
    }) => Promise<YmgMatchmakingTicketStatusResponse>
  }
  if (typeof sdkApi.getMatchmakingTicket === 'function') {
    const res = await sdkApi.getMatchmakingTicket({ gameId, ticketId })
    return {
      status: normalizeStatus(res.status),
      room: res.room ? normalizeRoom(res.room) : null,
    }
  }

  const res = await ymgFetch<YmgMatchmakingTicketStatusResponse>(
    `/games/${encodeURIComponent(gameId)}/matchmaking/tickets/${encodeURIComponent(ticketId)}`,
    { method: 'GET' },
  )
  return {
    status: normalizeStatus(res.status),
    room: res.room ? normalizeRoom(res.room) : null,
  }
}

/**
 * チケットをキャンセルする。冪等（404 / 既キャンセルは成功扱いで swallow）。
 *
 * MATCHED 後のキャンセル（409）は伝播する。UI 側で「もうマッチしています」を出すため。
 */
export async function cancelTicket(ticketId: string): Promise<void> {
  const gameId = getGameId()
  const preferFetch = getAuthState().status === 'signed_in' || (await hasCapacitorSessionToken())

  try {
    if (preferFetch) {
      await ymgFetch<{ ok: boolean }>(
        `/games/${encodeURIComponent(gameId)}/matchmaking/tickets/${encodeURIComponent(ticketId)}`,
        { method: 'DELETE' },
      )
      return
    }
    const sdk = await getYmgSdk()
    const sdkApi = sdk.api as unknown as {
      cancelMatchmakingTicket?: (p: {
        gameId: string
        ticketId: string
      }) => Promise<{ ok: boolean }>
    }
    if (typeof sdkApi.cancelMatchmakingTicket === 'function') {
      await sdkApi.cancelMatchmakingTicket({ gameId, ticketId })
      return
    }
    await ymgFetch<{ ok: boolean }>(
      `/games/${encodeURIComponent(gameId)}/matchmaking/tickets/${encodeURIComponent(ticketId)}`,
      { method: 'DELETE' },
    )
  } catch (err) {
    // 冪等性: 404 はキャンセル成功とみなす。それ以外は上流に伝える。
    const status = (err as { status?: number } | null)?.status
    if (status === 404) return
    throw err
  }
}

// ---------------------------------------------------------------------------
// High-level poll helper
// ---------------------------------------------------------------------------

/**
 * チケット状態を定期ポーリングし、MATCHED になるまで待機する。
 *
 * - `intervalMs` ごとに getTicketStatus を呼ぶ
 * - `timeoutMs` を超えた場合、自動でキャンセルして MatchmakingTimeoutError を throw
 * - `signal.abort()` が呼ばれた場合、キャンセルして MatchmakingCancelledError を throw
 * - サーバー側で TIMED_OUT / CANCELLED になった場合もそれぞれ対応するエラーを throw
 *
 * 呼び出し側は try/catch でエラー種別に応じた UI を出す。
 */
export async function pollUntilMatched(
  ticketId: string,
  options: PollUntilMatchedOptions = {},
): Promise<RoomInfo> {
  const intervalMs = options.intervalMs ?? MATCHMAKING_POLL_INTERVAL_MS
  const timeoutMs = options.timeoutMs ?? MATCHMAKING_TIMEOUT_MS
  const deadline = Date.now() + timeoutMs

  const sleep = (ms: number, signal?: AbortSignal) =>
    new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        cleanup()
        resolve()
      }, ms)
      const onAbort = () => {
        cleanup()
        reject(new MatchmakingCancelledError('aborted while waiting'))
      }
      const cleanup = () => {
        clearTimeout(timer)
        signal?.removeEventListener('abort', onAbort)
      }
      if (signal?.aborted) {
        onAbort()
        return
      }
      signal?.addEventListener('abort', onAbort)
    })

  while (true) {
    if (options.signal?.aborted) {
      // abort 時は呼び出し側でキャンセル API を叩く責任にしている
      throw new MatchmakingCancelledError()
    }

    if (Date.now() >= deadline) {
      // ローカルタイムアウト: サーバー側チケットもキャンセルしておく
      try {
        await cancelTicket(ticketId)
      } catch (err) {
        console.warn('[matchmaking] auto-cancel after timeout failed:', err)
      }
      throw new MatchmakingTimeoutError()
    }

    const { status, room } = await getTicketStatus(ticketId)
    options.onStatusUpdate?.(status)

    if (status === 'MATCHED' && room) {
      return room
    }
    if (status === 'TIMED_OUT') {
      throw new MatchmakingTimeoutError('server reported TIMED_OUT')
    }
    if (status === 'CANCELLED') {
      throw new MatchmakingCancelledError('server reported CANCELLED')
    }

    await sleep(intervalMs, options.signal)
  }
}
